import * as React from "react";
import { useNavigate } from "react-router";
import { Button, Chip, Label, Segmented } from "@moanhot/ui";
import { AdminProblem, useAdminApi, type AdminApplication, type QueueStatus } from "@/api/adminClient";

const VOLUME: Record<AdminApplication["expected_monthly_volume"], string> = {
  under_1k: "Under 1k / mo",
  "1k_10k": "1k to 10k / mo",
  "10k_50k": "10k to 50k / mo",
  "50k_plus": "50k+ / mo",
};

const when = (iso: string) => new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "UTC" }) + " UTC";

type Load = { state: "loading" } | { state: "ready"; rows: AdminApplication[] } | { state: "error"; message: string };

function Decide({ row, onDone }: { row: AdminApplication; onDone: (message: string) => void }) {
  const api = useAdminApi();
  const navigate = useNavigate();
  const [text, setText] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [problem, setProblem] = React.useState<string | null>(null);
  const reasonId = `reason-${row.id}`;

  const run = (action: "approve" | "reject") => {
    if (action === "reject" && text.trim().length < 3) return setProblem("A reason is required to reject. The partner sees it.");
    setBusy(true);
    setProblem(null);
    const call = action === "approve" ? api.approve(row.id, text.trim() || null) : api.reject(row.id, text.trim());
    call
      .then(() => onDone(action === "approve" ? `${row.tenant_name} approved.` : `${row.tenant_name} rejected.`))
      .catch((err: unknown) => {
        if (err instanceof AdminProblem && err.status === 401) return void navigate("/signin", { replace: true });
        setProblem(err instanceof AdminProblem ? `${err.detail}${err.requestId ? ` Request ${err.requestId}.` : ""}` : "Could not reach the server.");
        setBusy(false);
      });
  };

  return (
    <div className="mt-4 space-y-2 border-t border-line pt-4">
      <Label htmlFor={reasonId}>Note or reason</Label>
      <textarea
        id={reasonId}
        rows={2}
        maxLength={500}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Optional for approve. Required for reject, and shown to the partner."
        className="w-full rounded-sm border border-line-strong bg-raised px-2.5 py-2 text-sm text-ink placeholder:text-ink-3 focus-visible:border-focus focus-visible:outline-none"
      />
      {problem && (
        <p role="alert" className="text-sm text-err">
          {problem}
        </p>
      )}
      <div className="flex gap-2">
        <Button variant="primary" disabled={busy} onClick={() => run("approve")}>
          Approve
        </Button>
        <Button variant="secondary" disabled={busy} onClick={() => run("reject")}>
          Reject
        </Button>
      </div>
    </div>
  );
}

function Row({ row, onDone }: { row: AdminApplication; onDone: (message: string) => void }) {
  const signup = row.identities.map((i) => (i.provider === "google" ? `Google ${i.email ?? i.subject}` : `Telegram id ${i.subject}`)).join(", ");
  return (
    <li className="rounded-lg border border-line p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-md font-medium">{row.tenant_name}</h2>
        <span className="text-xs text-ink-3">Submitted {when(row.submitted_at)}</span>
      </div>
      <div className="mt-2 flex flex-wrap gap-2 text-xs">
        <Chip>{row.stage}</Chip>
        <Chip>{VOLUME[row.expected_monthly_volume]}</Chip>
        {row.decision && <Chip className={row.decision === "approved" ? "text-ok" : "text-err"}>{row.decision}</Chip>}
      </div>
      <p className="mt-3 max-w-[72ch] text-sm whitespace-pre-line text-ink">{row.use_case}</p>
      <dl className="mt-3 grid gap-x-6 gap-y-1 text-sm sm:grid-cols-[auto_1fr]">
        <dt className="text-ink-3">Contact</dt>
        <dd>{row.contact}</dd>
        {row.project_url && (
          <>
            <dt className="text-ink-3">Link</dt>
            <dd className="break-all">{row.project_url}</dd>
          </>
        )}
        <dt className="text-ink-3">Signed up with</dt>
        <dd className="font-mono text-xs">{signup || "unknown"}</dd>
        {row.decision_reason && (
          <>
            <dt className="text-ink-3">Note</dt>
            <dd>{row.decision_reason}</dd>
          </>
        )}
      </dl>
      {row.decision === null && <Decide row={row} onDone={onDone} />}
    </li>
  );
}

/** The application review queue (02-DASHBOARD-FEATURES 13a). */
export function Applications() {
  const api = useAdminApi();
  const navigate = useNavigate();
  const [status, setStatus] = React.useState<QueueStatus>("pending");
  const [load, setLoad] = React.useState<Load>({ state: "loading" });
  const [notice, setNotice] = React.useState<string | null>(null);
  const [tick, setTick] = React.useState(0);

  React.useEffect(() => {
    let live = true;
    api
      .getSession()
      .then((s) => {
        if (!s) {
          void navigate("/signin", { replace: true });
          return null;
        }
        return api.listApplications(status);
      })
      .then((page) => {
        if (live && page) setLoad({ state: "ready", rows: page.data });
      })
      .catch((err: unknown) => {
        if (!live) return;
        if (err instanceof AdminProblem && err.status === 401) return void navigate("/signin", { replace: true });
        setLoad({ state: "error", message: err instanceof AdminProblem ? err.detail : "Could not load applications." });
      });
    return () => {
      live = false;
    };
  }, [api, navigate, status, tick]);

  return (
    <div className="mx-auto w-full max-w-[1240px] px-4 py-6 md:px-8 md:py-8">
      <div className="flex flex-wrap items-center justify-between gap-3 pb-6">
        <h1 className="text-xl font-medium tracking-title">Applications</h1>
        <Segmented
          label="Status"
          value={status}
          onChange={(v) => {
            setLoad({ state: "loading" });
            setStatus(v);
          }}
          options={[
            { value: "pending", label: "Pending" },
            { value: "approved", label: "Approved" },
            { value: "rejected", label: "Rejected" },
          ]}
        />
      </div>
      {notice && (
        <p role="status" className="mb-4 text-sm text-ink-2">
          {notice}
        </p>
      )}
      {load.state === "loading" && <div className="h-40 animate-pulse rounded-lg border border-line bg-surface" aria-hidden />}
      {load.state === "error" && (
        <p role="alert" className="text-sm text-err">
          {load.message}
        </p>
      )}
      {load.state === "ready" && load.rows.length === 0 && (
        <section className="rounded-lg border border-dashed border-line-strong px-6 py-14 text-center">
          <h2 className="text-md font-medium">{status === "pending" ? "Nothing waiting for review" : `No ${status} applications`}</h2>
          <p className="mt-1 text-sm text-ink-2">New applications appear here as partners send them.</p>
        </section>
      )}
      {load.state === "ready" && load.rows.length > 0 && (
        <ul className="space-y-3">
          {load.rows.map((r) => (
            <Row
              key={r.id}
              row={r}
              onDone={(message) => {
                setNotice(message);
                setTick((n) => n + 1);
              }}
            />
          ))}
        </ul>
      )}
    </div>
  );
}
